import { useEffect, useMemo, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";
import { FileText, Save, ListChecks, Paperclip, X, Download, Plus, Eye } from "lucide-react";
import ChecklistEditor, { newItem } from "@/components/ChecklistEditor";

export default function IstruzioniDrawer({ open, onOpenChange, task }) {
  const { updateTask } = useData();
  const [text, setText] = useState("");
  const [checklist, setChecklist] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const fileRef = useRef(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && task) {
      setText(task.description || "");
      setChecklist(Array.isArray(task.checklist) ? task.checklist : []);
      setAttachments(Array.isArray(task.attachments) ? task.attachments : []);
      setDirty(false);
    }
  }, [open, task]);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    await addFiles(files);
  };

  const addFiles = async (files) => {
    if (!files || files.length === 0) return;
    try {
      const items = await Promise.all(files.map((f) => new Promise((res, rej) => {
        if (f.size > 5 * 1024 * 1024) return rej(new Error(`${f.name}: > 5MB`));
        const r = new FileReader();
        r.onload = () => res({
          id: `att_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          name: f.name, type: f.type, size: f.size, data_url: r.result,
        });
        r.onerror = () => rej(r.error);
        r.readAsDataURL(f);
      })));
      setAttachments((p) => [...p, ...items]);
      setDirty(true);
    } catch (err) {
      toast.error(err.message || "Errore file");
    }
  };

  const [dragOver, setDragOver] = useState(false);
  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) await addFiles(files);
  };

  const total = checklist.length;
  const done = useMemo(() => checklist.filter((c) => c.done).length, [checklist]);

  const toggleChecklist = () => {
    if (checklist.length > 0) {
      setChecklist([]);
    } else {
      setChecklist([newItem()]);
    }
    setDirty(true);
  };

  const save = async () => {
    if (!task) return;
    setSaving(true);
    try {
      await updateTask(task.id, {
        description: text,
        checklist: checklist.filter((c) => c.text.trim().length > 0 || c.done || (c.note && c.note.trim().length > 0)),
        attachments,
      });
      setDirty(false);
      toast.success("Istruzioni salvate");
    } catch {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={`tp-glass-strong border-white/10 text-white w-full sm:max-w-md flex flex-col transition-colors ${
          dragOver ? "ring-2 ring-fuchsia-400/70 bg-fuchsia-950/20" : ""
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
        onDrop={onDrop}
        data-testid="istruzioni-dropzone"
      >
        <SheetHeader>
          <SheetTitle className="text-white text-lg flex items-center gap-2">
            <FileText size={18} className="text-fuchsia-300" /> Istruzioni
          </SheetTitle>
          {task && (
            <div className="text-xs text-zinc-400 truncate">{task.title}</div>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              Testo libero
            </div>
            <button
              type="button"
              onClick={() => {
                toggleChecklist();
              }}
              data-testid="drawer-toggle-checklist"
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] border transition-colors ${
                checklist.length > 0
                  ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-300"
                  : "bg-white/5 border-white/10 text-zinc-400 hover:text-white hover:bg-white/10"
              }`}
            >
              <ListChecks size={12} />
              Checklist
            </button>
          </div>
          <Textarea
            data-testid="istruzioni-textarea"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setDirty(true);
            }}
            placeholder="Aggiungi qui le istruzioni… i link diventano cliccabili nell'anteprima sotto"
            className="bg-white/5 border-white/10 text-white min-h-[140px] text-sm leading-relaxed"
          />
          {text && text.match(/(https?:\/\/|www\.)/i) && (
            <div
              data-testid="istruzioni-preview"
              className="rounded-xl border border-white/5 bg-white/[0.02] p-3 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap break-words"
            >
              {text
                .split(/(\s+)/)
                .map((tok, i) => {
                  const isHttp = /^https?:\/\/\S+$/i.test(tok);
                  const isWww = /^www\.\S+$/i.test(tok);
                  if (isHttp || isWww) {
                    const href = isHttp ? tok : `https://${tok}`;
                    return (
                      <a
                        key={i}
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        className="text-fuchsia-300 hover:text-fuchsia-200 underline break-all"
                      >
                        {tok}
                      </a>
                    );
                  }
                  return <span key={i}>{tok}</span>;
                })}
            </div>
          )}

          {checklist.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 space-y-2">
              <div className="flex items-center justify-between px-1">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Checklist
                </div>
                {total > 0 && (
                  <div
                    className="text-[11px] text-zinc-400 tabular-nums"
                    data-testid="checklist-progress"
                  >
                    {done}/{total}
                  </div>
                )}
              </div>
              <ChecklistEditor
                items={checklist}
                onChange={(next) => {
                  setChecklist(next);
                  setDirty(true);
                }}
                testIdPrefix="drawer-checklist"
              />
            </div>
          )}
        </div>

        <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={fileRef}
              data-testid="istruzioni-file-input"
              type="file"
              multiple
              className="hidden"
              onChange={onFile}
            />
            <Button
              onClick={() => fileRef.current?.click()}
              data-testid="istruzioni-attach-button"
              type="button"
              className="h-9 rounded-full tp-brand-gradient text-white text-xs px-3 shadow-md shadow-fuchsia-600/30"
              title="Aggiungi file o media"
            >
              <Plus size={14} className="mr-1" strokeWidth={2.6} />
              File
            </Button>
            {attachments.length > 0 && (
              <span className="text-[11px] text-zinc-400">
                {attachments.length} allegat{attachments.length === 1 ? "o" : "i"} · si eliminano al completamento
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {dirty && (
              <span className="text-[11px] text-amber-300">Non salvato</span>
            )}
            <Button
              onClick={save}
              disabled={saving || !dirty}
              data-testid="istruzioni-save-button"
              className="tp-brand-btn rounded-xl"
            >
              <Save size={14} className="mr-1.5" />
              {saving ? "Salvo..." : "Salva"}
            </Button>
          </div>
        </div>
        {attachments.length > 0 && (
          <div className="mt-2 space-y-1">
            {attachments.map((a) => {
              const isImg = (a.type || "").startsWith("image/");
              return (
                <div
                  key={a.id}
                  data-testid={`attachment-${a.id}`}
                  className="flex items-center gap-2 rounded-lg bg-white/5 border border-white/10 p-2"
                >
                  <a
                    href={a.data_url}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0"
                    title="Anteprima"
                    data-testid={`attachment-preview-${a.id}`}
                  >
                    {isImg ? (
                      <img
                        src={a.data_url}
                        alt={a.name}
                        className="h-10 w-10 rounded object-cover hover:opacity-80 transition-opacity"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded bg-white/10 flex items-center justify-center hover:bg-white/15 transition-colors">
                        <Paperclip size={16} className="text-zinc-400" />
                      </div>
                    )}
                  </a>
                  <a
                    href={a.data_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 min-w-0 text-xs text-zinc-200 truncate hover:text-white"
                    title="Apri"
                  >
                    {a.name}
                  </a>
                  <a
                    href={a.data_url}
                    target="_blank"
                    rel="noreferrer"
                    className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-white/10"
                    title="Anteprima"
                    data-testid={`attachment-view-${a.id}`}
                  >
                    <Eye size={14} />
                  </a>
                  <a
                    href={a.data_url}
                    download={a.name}
                    className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-white/10"
                    title="Scarica"
                    data-testid={`attachment-download-${a.id}`}
                  >
                    <Download size={14} />
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      setAttachments((p) => p.filter((x) => x.id !== a.id));
                      setDirty(true);
                    }}
                    className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                    title="Rimuovi"
                    data-testid={`attachment-remove-${a.id}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
